/**
 * SanPlumb — useThreeScene (Fase 3d)
 *
 * Hook que encapsula el ciclo de vida imperativo de Three.js:
 *   - renderer WebGL + escena + cámara perspectiva
 *   - órbita esférica (theta/phi/dist) por drag + zoom por rueda
 *   - auto-rotación suave cuando no se arrastra
 *   - ResizeObserver para mantener el aspect ratio
 *   - render loop con requestAnimationFrame
 *
 * NO construye contenido (tuberías, muros): eso lo hace el engine
 * `buildSceneFromData`. El hook expone la `scene` y la cámara vía un objeto
 * de control para que el componente repueble cuando cambian los datos.
 *
 * Cámara y parámetros de órbita replicados del diseño aprobado (viewer3d.jsx).
 */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { CSS2DRenderer } from 'three/examples/jsm/renderers/CSS2DRenderer.js';
import type { CameraView } from '../types/ui';

export interface SceneControls {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  /** Aplica un preset de vista (ISO/TOP/ELEV/LAT). */
  setView: (v: CameraView) => void;
  /** Reinicia el reloj de animación de entrada. */
  replay: () => void;
  /** Punto al que mira la cámara (centro de la escena). */
  setTarget: (x: number, y: number, z: number) => void;
  /** Habilita/deshabilita la auto-rotación suave de la cámara. */
  setAutoRotate: (on: boolean) => void;
  /** Registra los meshes a animar (tuberías crecen, accesorios aparecen). */
  setAnimatables: (pipes: THREE.Object3D[], fittings: THREE.Object3D[]) => void;
}

interface OrbitState {
  theta: number;
  phi: number;
  dist: number;
  tx: number;
  ty: number;
  tz: number;
  dragging: boolean;
  lx: number;
  ly: number;
  t0: number;
  autoRotate: boolean;
  animPipes: THREE.Object3D[];
  animFittings: THREE.Object3D[];
}

export interface UseThreeSceneResult {
  mountRef: React.RefObject<HTMLDivElement>;
  controlsRef: React.RefObject<SceneControls | null>;
}

/**
 * Inicializa la escena una sola vez (al montar) y devuelve refs estables.
 * El `onReady` se llama cuando la escena está lista para poblarse.
 */
export function useThreeScene(onReady?: (c: SceneControls) => void): UseThreeSceneResult {
  const mountRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<SceneControls | null>(null);
  const onReadyRef = useRef(onReady);
  onReadyRef.current = onReady;

  useEffect(() => {
    const el = mountRef.current;
    if (!el) return;
    const w = el.clientWidth || 800;
    const h = el.clientHeight || 600;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x040b13);
    scene.fog = new THREE.Fog(0x040b13, 8, 20);

    const camera = new THREE.PerspectiveCamera(28, w / h, 0.1, 100);

    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    el.appendChild(renderer.domElement);

    // CSS2DRenderer: overlay HTML para etiquetas de diámetro (toggle MEDIDAS).
    // Las CSS2DObject las crea sceneBuilder cuando layers.medidas === true.
    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(w, h);
    labelRenderer.domElement.style.position = 'absolute';
    labelRenderer.domElement.style.top = '0';
    labelRenderer.domElement.style.left = '0';
    labelRenderer.domElement.style.pointerEvents = 'none';
    el.appendChild(labelRenderer.domElement);

    // Estado de órbita
    const o: OrbitState = {
      theta: Math.PI * 0.28,
      phi: 0.5,
      dist: 6.2,
      tx: 1.25,
      ty: 0.9,
      tz: 1.0,
      dragging: false,
      lx: 0,
      ly: 0,
      t0: performance.now(),
      autoRotate: true,
      animPipes: [],
      animFittings: [],
    };

    // Animación de entrada (diseño aprobado): tuberías crecen escalando su eje
    // de longitud de forma escalonada; los accesorios aparecen tras ellas.
    const STAGGER = 55; // ms entre tuberías
    const DURATION = 300; // ms de crecimiento de cada tubería
    function animateEntrance(): void {
      const t = performance.now() - o.t0;
      const pipes = o.animPipes;
      for (let i = 0; i < pipes.length; i++) {
        const k = Math.min(1, Math.max(0, (t - i * STAGGER) / DURATION));
        const e = 1 - Math.pow(1 - k, 3); // ease-out cúbico
        pipes[i].scale.y = Math.max(0.001, e);
      }
      const lastEnd = (pipes.length - 1) * STAGGER + DURATION;
      const fittings = o.animFittings;
      for (let i = 0; i < fittings.length; i++) {
        const start = lastEnd * 0.5 + i * 22;
        const k = Math.min(1, Math.max(0, (t - start) / 200));
        const e = 1 - Math.pow(1 - k, 2); // ease-out cuadrático
        fittings[i].scale.setScalar(Math.max(0.001, e));
      }
    }

    function place(): void {
      camera.position.x = o.tx + o.dist * Math.cos(o.phi) * Math.sin(o.theta);
      camera.position.z = o.tz + o.dist * Math.cos(o.phi) * Math.cos(o.theta);
      camera.position.y = o.ty + o.dist * Math.sin(o.phi);
      camera.lookAt(o.tx, o.ty, o.tz);
    }
    place();

    function onPointerDown(e: PointerEvent): void {
      o.dragging = true;
      o.lx = e.clientX;
      o.ly = e.clientY;
    }
    function onPointerUp(): void {
      o.dragging = false;
    }
    function onPointerMove(e: PointerEvent): void {
      if (!o.dragging) return;
      o.theta -= (e.clientX - o.lx) * 0.01;
      o.phi = Math.max(0.05, Math.min(Math.PI / 2 - 0.05, o.phi + (e.clientY - o.ly) * 0.01));
      o.lx = e.clientX;
      o.ly = e.clientY;
      place();
    }
    function onWheel(e: WheelEvent): void {
      e.preventDefault();
      o.dist = Math.max(2.5, Math.min(14, o.dist + e.deltaY * 0.005));
      place();
    }

    renderer.domElement.addEventListener('pointerdown', onPointerDown);
    window.addEventListener('pointerup', onPointerUp);
    window.addEventListener('pointermove', onPointerMove);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });

    const ro = new ResizeObserver(() => {
      const cw = el.clientWidth;
      const ch = el.clientHeight;
      if (cw === 0 || ch === 0) return;
      renderer.setSize(cw, ch);
      labelRenderer.setSize(cw, ch);
      camera.aspect = cw / ch;
      camera.updateProjectionMatrix();
    });
    ro.observe(el);

    let rafId = 0;
    function tick(): void {
      if (!o.dragging && o.autoRotate) {
        o.theta += 0.0006; // auto-rotación suave
        place();
      }
      animateEntrance();
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
      rafId = requestAnimationFrame(tick);
    }
    tick();

    const controls: SceneControls = {
      scene,
      camera,
      setView(v: CameraView) {
        if (v === 'ISO') {
          o.theta = Math.PI * 0.28;
          o.phi = 0.5;
          o.dist = 6.2;
        } else if (v === 'TOP') {
          o.theta = 0;
          o.phi = 1.42;
          o.dist = 6.5;
        } else if (v === 'ELEV') {
          o.theta = 0;
          o.phi = 0.04;
          o.dist = 5.5;
        } else if (v === 'LAT') {
          o.theta = Math.PI * 0.5;
          o.phi = 0.04;
          o.dist = 5.5;
        }
        place();
      },
      replay() {
        o.t0 = performance.now();
      },
      setTarget(x: number, y: number, z: number) {
        o.tx = x;
        o.ty = y;
        o.tz = z;
        place();
      },
      setAutoRotate(on: boolean) {
        o.autoRotate = on;
      },
      setAnimatables(pipes: THREE.Object3D[], fittings: THREE.Object3D[]) {
        o.animPipes = pipes;
        o.animFittings = fittings;
        // Estado inicial: todo colapsado, listo para crecer desde t0 (replay).
        pipes.forEach((p) => (p.scale.y = 0.001));
        fittings.forEach((f) => f.scale.setScalar(0.001));
        o.t0 = performance.now();
      },
    };
    controlsRef.current = controls;
    onReadyRef.current?.(controls);

    return () => {
      cancelAnimationFrame(rafId);
      ro.disconnect();
      renderer.domElement.removeEventListener('pointerdown', onPointerDown);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('pointermove', onPointerMove);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.dispose();
      if (renderer.domElement.parentNode === el) {
        el.removeChild(renderer.domElement);
      }
      if (labelRenderer.domElement.parentNode === el) {
        el.removeChild(labelRenderer.domElement);
      }
      controlsRef.current = null;
    };
  }, []);

  return { mountRef, controlsRef };
}
